import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import api from '../api/api';
import { reportPreferenceRecovery, reportPreferenceRecoveryMessage } from '../utils/reportPreferenceRecovery';
import { extractApiErrorMessage } from '../utils/apiError';
import { buildRestaurantEmployeePrintHtml } from '../utils/restaurantEmployeePrint';
import { printHtmlInHiddenFrame } from '../utils/printDocument';
import { attendanceMonthRange } from './RestaurantAttendanceHistory';
import { loadRestaurantAttendanceReport } from '../utils/restaurantAttendanceReport';
import { hasRestaurantAttendanceFields } from '../utils/restaurantReportFields';
import PersonalReportDeliveryPanel from './PersonalReportDeliveryPanel';
import RestaurantReportPreview from './RestaurantReportPreview';

const fields = [
  ['position', 'Посада'],
  ['attendance', 'Дані табеля'],
  ['attendance-intervals', 'Інтервали приходу й виходу'],
  ['attendance-hours', 'Години табеля'],
  ['attendance-coefficients', 'Коефіцієнти табеля'],
  ['attendance-notes', 'Примітки табеля'],
] as const;
type Field = typeof fields[number][0];
type Preferences = { fields: Field[]; version: number };
type OwnEmployment = { employment: { id: number; fullName: string; position: string } };

const PersonalEmployeeTimesheet: React.FC<{ month: string; operatingGroupId?: string | number }> = ({
  month, operatingGroupId,
}) => {
  const [preferences, setPreferences] = useState<Preferences>({ fields: [], version: 0 });
  const [ready, setReady] = useState(false);
  const [recovering, setRecovering] = useState(false);
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showDelivery, setShowDelivery] = useState(false);
  const [preview, setPreview] = useState<{ html: string; context: string; generation: number } | null>(null);
  const [error, setError] = useState('');
  const request = useRef<AbortController | null>(null);
  const frameRequest = useRef<AbortController | null>(null);
  const generation = useRef(0);
  const fieldsKey = preferences.fields.join('\u001f');
  const context = JSON.stringify([month, operatingGroupId ?? null, fieldsKey]);
  const previewHtml = preview?.context === context && preview.generation === generation.current
    ? preview.html : null;

  useEffect(() => {
    const controller = new AbortController();
    setReady(false);
    setRecovering(false);
    void api.get<Preferences>('/personal-report-delivery/me/fields/employee-timesheet', {
      signal: controller.signal,
    }).then(response => {
      if (controller.signal.aborted) return;
      setPreferences(response.data);
      setReady(true);
    }).catch(failure => {
      if (!controller.signal.aborted) {
        const recovery = reportPreferenceRecovery(failure, 'employee-timesheet', 'print');
        if (recovery) {
          setPreferences({ fields: [], version: recovery.version });
          setRecovering(true);
          setReady(true);
          return;
        }
        setError(extractApiErrorMessage(failure, 'Не вдалося завантажити поля особистого табеля.'));
      }
    });
    return () => controller.abort();
  }, [operatingGroupId]);

  useLayoutEffect(() => {
    generation.current += 1;
    request.current?.abort();
    frameRequest.current?.abort();
    request.current = null;
    setBusy(false);
    setSaving(false);
    setPreview(null);
    setError('');
    return () => {
      ++generation.current;
      request.current?.abort();
      frameRequest.current?.abort();
      request.current = null;
    };
  }, [month, fieldsKey, operatingGroupId]);

  const savePreferences = async () => {
    if (!ready || saving || busy) return false;
    const saveGeneration = generation.current;
    setSaving(true);
    setError('');
    try {
      const response = await api.put<Preferences>(
        '/personal-report-delivery/me/fields/employee-timesheet', preferences);
      if (generation.current !== saveGeneration) return false;
      setPreferences(response.data);
      setRecovering(false);
      return true;
    } catch (failure) {
      if (generation.current === saveGeneration)
        setError(extractApiErrorMessage(failure, 'Не вдалося зберегти поля особистого табеля.'));
      return false;
    } finally {
      if (generation.current === saveGeneration) setSaving(false);
    }
  };

  const print = async (previewOnly = false) => {
    if (!ready || recovering || busy || saving) return;
    const printGeneration = generation.current;
    const controller = new AbortController();
    request.current?.abort();
    request.current = controller;
    const valid = () => !controller.signal.aborted && request.current === controller &&
      generation.current === printGeneration;
    setBusy(true);
    setError('');
    setPreview(null);
    try {
      const detail = await api.get<OwnEmployment>('/restaurant-personnel/me', { signal: controller.signal });
      if (!valid()) return;
      const employment = detail.data.employment;
      if (!Number.isSafeInteger(employment.id) || employment.id <= 0)
        throw new Error('Не вдалося підтвердити особисте працевлаштування. Повторіть друк.');
      const attendance = hasRestaurantAttendanceFields(preferences.fields)
        ? await loadRestaurantAttendanceReport('/restaurant-personnel/me/attendance',
          employment.id, attendanceMonthRange(month), controller.signal, valid)
        : [];
      if (!valid()) return;
      const html = buildRestaurantEmployeePrintHtml({
        template: 'employee-timesheet',
        month,
        employee: {
          fullName: detail.data.employment.fullName,
          position: detail.data.employment.position,
          attendance,
        },
        canViewSalary: false,
        canViewBusinessRevenue: false,
        payrollHistory: null,
        payrollHistoryLoading: false,
        payrollHistoryError: '',
        fields: preferences.fields,
      });
      if (!valid()) return;
      if (previewOnly) setPreview({ html, context, generation: printGeneration });
      else {
        frameRequest.current?.abort();
        frameRequest.current = controller;
        printHtmlInHiddenFrame(html, {
          signal: controller.signal,
          onError: failure => {
            if (!controller.signal.aborted && generation.current === printGeneration)
              setError(extractApiErrorMessage(failure, 'Не вдалося підготувати табель до друку.'));
          },
        });
      }
    } catch (failure) {
      if (valid())
        setError(extractApiErrorMessage(failure, 'Не вдалося завантажити особистий табель.'));
    } finally {
      if (request.current === controller) {
        if (frameRequest.current !== controller) controller.abort();
        request.current = null;
        setBusy(false);
      }
    }
  };

  return <section aria-label="Особистий місячний табель">
    <h3>Особистий місячний табель</h3>
    <p>Показуються лише ваші інтервали за вибраний місяць; зарплатні дані та записи інших працівників не завантажуються.</p>
    {recovering && <p role="alert">{reportPreferenceRecoveryMessage}</p>}
    <fieldset disabled={!ready || busy || saving}>
      <legend>Поля особистого табеля</legend>
      {fields.map(([key, label]) => <label key={key}>
        <input type="checkbox" checked={preferences.fields.includes(key)}
          onChange={event => {
            request.current?.abort();
            request.current = null;
            setPreview(null);
            setPreferences(current => ({
              ...current,
              fields: event.target.checked
                ? [...current.fields, key]
                : current.fields.filter(field => field !== key),
            }));
          }} />
        {label}
      </label>)}
    </fieldset>
    <div className="restaurant-personnel__actions">
      <button type="button" disabled={!ready || busy || saving} onClick={() => void savePreferences()}>
        Зберегти поля
      </button>
      <button type="button" disabled={!ready || recovering || busy || saving} onClick={() => void print(true)}>
        Попередній перегляд
      </button>
      <button type="button" disabled={!ready || recovering || busy || saving} onClick={() => void print()}>
        Друк / PDF
      </button>
    </div>
    <button type="button" disabled={!ready || recovering || busy || saving} onClick={() => setShowDelivery(value => !value)}>
      {showDelivery ? 'Приховати приватну відправку' : 'Приватна відправка'}
    </button>
    {showDelivery && <PersonalReportDeliveryPanel
      reportBlocked={!ready || recovering}
      template="employee-timesheet"
      year={Number(month.slice(0, 4))}
      month={Number(month.slice(5, 7))}
      scopeKey={String(operatingGroupId ?? '')}
    />}
    {error && <p role="alert">{error}</p>}
    <RestaurantReportPreview html={previewHtml} title={`Особистий табель за ${month}`}
      onClose={() => setPreview(null)} />
  </section>;
};

export default PersonalEmployeeTimesheet;
